import type React from "react";
import { useState, useEffect, useRef } from "react";

interface Option {
    value: string;
    text: string;
}

interface MultiSelectProps {
    label: string;
    options: Option[];
    defaultSelected?: string[];
    value?: string[];
    onChange?: (selected: string[]) => void;
    disabled?: boolean;
    placeholder?: string;
    searchPlaceholder?: string;
}

const MultiSelectSearchable: React.FC<MultiSelectProps> = ({
    label,
    options,
    defaultSelected = [],
    value,
    onChange,
    disabled = false,
    placeholder = "Select options",
    searchPlaceholder = "Search...",
}) => {
    const isControlled = value !== undefined;
    const [internalSelected, setInternalSelected] =
        useState<string[]>(defaultSelected);
    const selectedOptions = isControlled ? value : internalSelected;
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const filteredOptions = options.filter((option) =>
        option.text.toLowerCase().includes(searchTerm.toLowerCase())
    );

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
                setSearchTerm("");
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            return () =>
                document.removeEventListener("mousedown", handleClickOutside);
        }
    }, [isOpen]);

    const updateSelection = (newSelected: string[]) => {
        if (!isControlled) setInternalSelected(newSelected);
        onChange?.(newSelected);
    };

    const toggleDropdown = () => {
        if (!disabled) {
            setIsOpen((prev) => {
                if (prev) setSearchTerm("");
                return !prev;
            });
            setFocusedIndex(-1);
        }
    };

    const handleSelect = (optionValue: string) => {
        const newSelected = selectedOptions.includes(optionValue)
            ? selectedOptions.filter((v) => v !== optionValue)
            : [...selectedOptions, optionValue];
        updateSelection(newSelected);
    };

    const removeOption = (optionValue: string) => {
        updateSelection(selectedOptions.filter((v) => v !== optionValue));
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (disabled) return;

        if (e.key === "Enter" && !isOpen) {
            e.preventDefault();
            setIsOpen(true);
            return;
        }

        if (!isOpen) return;

        switch (e.key) {
            case "Enter":
                e.preventDefault();
                if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
                    handleSelect(filteredOptions[focusedIndex].value);
                }
                break;
            case "Escape":
                e.preventDefault();
                setIsOpen(false);
                setSearchTerm("");
                break;
            case "ArrowDown":
                e.preventDefault();
                setFocusedIndex((prev) =>
                    prev < filteredOptions.length - 1 ? prev + 1 : 0
                );
                break;
            case "ArrowUp":
                e.preventDefault();
                setFocusedIndex((prev) =>
                    prev > 0 ? prev - 1 : filteredOptions.length - 1
                );
                break;
        }
    };

    return (
        <div className="w-full" ref={dropdownRef}>
            <label
                className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
                id={`${label}-label`}
            >
                {label}
            </label>

            <div className="relative z-20 inline-block w-full">
                <div className="relative flex flex-col items-center">
                    <div
                        onClick={toggleDropdown}
                        onKeyDown={handleKeyDown}
                        className="w-full"
                        role="combobox"
                        aria-expanded={isOpen}
                        aria-haspopup="listbox"
                        aria-labelledby={`${label}-label`}
                        aria-disabled={disabled}
                        tabIndex={disabled ? -1 : 0}
                    >
                        <div
                            className={`mb-2 flex min-h-11  rounded-lg border border-gray-300 py-1.5 pl-3 pr-3 shadow-theme-xs outline-hidden transition focus-within:border-brand-300 focus-within:shadow-focus-ring dark:border-gray-700 dark:bg-gray-900 dark:focus-within:border-brand-300 ${disabled
                                    ? "opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-800"
                                    : "cursor-pointer"
                                }`}
                        >
                            <div className="flex flex-wrap flex-auto gap-2">
                                {selectedOptions.length > 0 ? (
                                    selectedOptions.map((value) => {
                                        const text =
                                            options.find((opt) => opt.value === value)?.text || value;
                                        return (
                                            <div
                                                key={value}
                                                className="group flex items-center justify-center rounded-full border-[0.7px] border-transparent bg-gray-100 py-1 pl-2.5 pr-2 text-sm text-gray-800 hover:border-gray-200 dark:bg-gray-800 dark:text-white/90 dark:hover:border-gray-800"
                                            >
                                                <span className="flex-initial max-w-full">{text}</span>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (!disabled) removeOption(value);
                                                    }}
                                                    disabled={disabled}
                                                    className="pl-2 text-gray-500 cursor-pointer group-hover:text-gray-400 dark:text-gray-400 disabled:cursor-not-allowed"
                                                    aria-label={`Remove ${text}`}
                                                >
                                                    <svg
                                                        className="fill-current"
                                                        width="14"
                                                        height="14"
                                                        viewBox="0 0 14 14"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            clipRule="evenodd"
                                                            d="M3.40717 4.46881C3.11428 4.17591 3.11428 3.70104 3.40717 3.40815C3.70006 3.11525 4.17494 3.11525 4.46783 3.40815L6.99943 5.93975L9.53095 3.40822C9.82385 3.11533 10.2987 3.11533 10.5916 3.40822C10.8845 3.70112 10.8845 4.17599 10.5916 4.46888L8.06009 7.00041L10.5916 9.53193C10.8845 9.82482 10.8845 10.2997 10.5916 10.5926C10.2987 10.8855 9.82385 10.8855 9.53095 10.5926L6.99943 8.06107L4.46783 10.5927C4.17494 10.8856 3.70006 10.8856 3.40717 10.5927C3.11428 10.2998 3.11428 9.8249 3.40717 9.53201L5.93877 7.00041L3.40717 4.46881Z"
                                                        />
                                                    </svg>
                                                </button>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="w-full h-full p-1 pr-2 text-sm text-gray-400 dark:text-gray-500 pointer-events-none">
                                        {placeholder}
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center self-start py-1 pl-1 pr-1 w-7">
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleDropdown();
                                    }}
                                    disabled={disabled}
                                    className="w-5 h-5 text-gray-700 outline-hidden cursor-pointer focus:outline-hidden dark:text-gray-400 disabled:cursor-not-allowed"
                                >
                                    <svg
                                        className={`stroke-current transition-transform ${isOpen ? "rotate-180" : ""
                                            }`}
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M4.79175 7.39551L10.0001 12.6038L15.2084 7.39551"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    {isOpen && (
                        <div
                            className="absolute left-0 z-40 w-full bg-white rounded-lg shadow-xl top-full dark:bg-gray-900 border border-gray-200 dark:border-gray-800"
                            onClick={(e) => e.stopPropagation()}
                            role="listbox"
                            aria-label={label}
                        >
                            <div className="p-2 border-b border-gray-100 dark:border-gray-800">
                                <input
                                    type="text"
                                    autoFocus
                                    placeholder={searchPlaceholder}
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setFocusedIndex(0);
                                    }}
                                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border-none rounded-md outline-none focus:ring-1 focus:ring-brand-500/50 dark:text-white"
                                />
                            </div>
                            <div className="max-h-60 overflow-y-auto">
                                {filteredOptions.length > 0 ? (
                                    filteredOptions.map((option, index) => {
                                        const isSelected = selectedOptions.includes(option.value);
                                        const isFocused = index === focusedIndex;

                                        return (
                                            <div
                                                key={option.value}
                                                className={`hover:bg-brand-500/5 w-full cursor-pointer border-b border-gray-50 last:border-none dark:border-gray-800/50 ${isFocused ? "bg-brand-500/10" : ""
                                                    } ${isSelected ? "text-brand-500 font-medium" : ""}`}
                                                onClick={() => handleSelect(option.value)}
                                                role="option"
                                                aria-selected={isSelected}
                                            >
                                                <div className="relative flex w-full items-center p-2.5 px-4">
                                                    <div className="flex-grow text-sm text-gray-800 dark:text-white/90">
                                                        {option.text}
                                                    </div>
                                                    {isSelected && (
                                                        <svg className="w-4 h-4 text-brand-500" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="p-4 text-sm text-gray-500 text-center italic">
                                        No results found for "{searchTerm}"
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MultiSelectSearchable;
